import React, { FC, useCallback, useContext } from 'react';
import Pagination from '../../shared/common/Pagination';
import TotalAmountBadges from '../../shared/common/TotalAmountBadges';
import TotalDropDown from '../selectionlists/TotalDropDown';
import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { useOrderLists } from '../../shared/common/useOrderLists';
import RoutingUtils from '../../shared/utils/utils-routing';
import { SelectionContext } from './context/SelectionContext';
import SelectionBulkContainer from '../selectionlists/header/SelectionBulkContainer';
import { RenewalType } from './utils/renewalTableTileTypes';
import SelectionBulkMenu from '../../shared/components/SelectionBulkMenu';
import { UseMutateFunction } from '@tanstack/react-query';
import { EditAllRenewalType, EditRenewalType } from '../../api/subscription-management/renewals/bulkChangeRenewalType';
import { AxiosError } from 'axios';
import { SummaryTotalsOut } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useAddRenewalsToCart } from '../../api/shopping-cart/useAddRenewalsToCart';
import { useAddRenewalsToList } from '../../api/selection-list/useAddRenewalsToList';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { RenewalAddOptions } from '../../../models/ListContentCheckOptions';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { Orderlist } from '../../../models/OrderList';
import { COLLECTION_NAVIGATION_V2, useFeature } from '../../shared/utils/togglz';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';

type Props = {
    totals?: SummaryTotalsOut;
    isLoadingTotals?: boolean;
    renewalLines?: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    productIds: string[];
    editAllRenewalTypeMutation: UseMutateFunction<void, AxiosError, EditAllRenewalType, any>;
    editRenewalTypeMutation: UseMutateFunction<void, AxiosError, EditRenewalType, any>;
};

export const ManageRenewalsHeader: FC<Props> = ({
    renewalLines,
    page,
    pageSize,
    onPageChange,
    totals,
    isLoadingTotals,
    productIds,
    editAllRenewalTypeMutation,
    editRenewalTypeMutation
}) => {
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);
    const { orderlists, createNewList } = useOrderLists();
    const { t } = useTranslation();
    const { isInSelectionMode, cancelSelection, isSelectAll, toggleProductSelection, selectAllProducts, selection } =
        useContext(SelectionContext);

    const excludedProductIds = productIds.filter(productId => !selection.includes(productId));

    const handleChangeRequiredLoan = (type: RenewalType) => {
        if (isSelectAll) {
            editAllRenewalTypeMutation({ targetType: type, excludedProductIds });
            cancelSelection();
        } else {
            editRenewalTypeMutation({ targetType: type, includedProductIds: selection });
            cancelSelection();
        }
    };

    const { isLoading: isLoadingAddToCart, addToCartBulk } = useAddRenewalsToCart();
    const { isLoading: isLoadingAddToList, addToListBulk } = useAddRenewalsToList();

    const onAction = useCallback(
        (performChecks: boolean, options: RenewalAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToListBulk(options.listId, options.totalValue, performChecks);
            } else {
                return addToCartBulk(options.totalValue, performChecks);
            }
        },
        [addToCartBulk, addToListBulk]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<RenewalAddOptions>({ onAction });

    const handleAddToListBulk = useCallback(
        (list: Orderlist) => {
            changePrice({ target: ListActionTarget.List, listId: list.id, totalValue: totals?.totalValue ?? -1 });
        },
        [changePrice, totals?.totalValue]
    );

    const handleAddToCartBulk = useCallback(() => {
        changePrice({ target: ListActionTarget.Cart, totalValue: totals?.totalValue ?? -1 });
    }, [changePrice, totals?.totalValue]);

    return (
        <>
            <SelectionBulkContainer
                movedUp={isInSelectionMode}
                isSticky={false}
                headerComponent={
                    <div className="action-bar">
                        <div className="breadcrumb">
                            <h4>{t('shop.subscriptions.subscriptionManagement')}</h4>
                            <p className="material-icons md-big">keyboard_arrow_right</p>
                            <h4> {t('shop.subscriptions.manageRenewals')}</h4>
                        </div>

                        <div className="spacer" />

                        <TotalAmountBadges
                            epressAmount={totals?.epressAmount}
                            epressValue={totals?.epressValue}
                            currency={totals?.currency}
                            isLoading={isLoadingTotals}
                        />
                        <TotalDropDown
                            ebooksAmountVisible={false}
                            eaudiobooksAmountVisible={false}
                            summary={totals}
                            textTotalLine={t('shop.subscriptions.renewalTotal')}
                        />
                        <SelectionListDropDown
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.POSITIVE_INVERT}
                            label={t('addToList')}
                            lists={orderlists}
                            onCreateNewListClicked={createNewList}
                            onListClick={handleAddToListBulk}
                            disabled={isLoadingAddToList}
                        />
                        <Button
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.POSITIVE}
                            icon="shopping_cart"
                            text={t('addToCart')}
                            onClick={handleAddToCartBulk}
                            disabled={isLoadingAddToCart}
                            isLoading={isLoadingAddToCart}
                        />
                    </div>
                }
                bulkMenuComponent={
                    <SelectionBulkMenu
                        isChangeRequiredLoansEnabled={true}
                        handleChangeRequiredLoan={handleChangeRequiredLoan}
                        isAddToCartEnabled={false}
                        cancelSelection={cancelSelection}
                        selectedCount={selection.length}
                        isNothingSelected={!isSelectAll || selection.length === 0}
                    />
                }
                headerHeight={64}
            />

            <div className="filter-bar">
                <div className="layout-column-33 fl-left renewal-product-selection">
                    <p className="total-products-label">
                        {renewalLines ? (
                            <>
                                {t('shop.subscriptions.subscriptionCount', {
                                    count: renewalLines
                                })}
                            </>
                        ) : null}
                    </p>
                    <button onClick={toggleProductSelection}>{isInSelectionMode ? t('cancel') : t('select')}</button>
                    <button onClick={() => selectAllProducts(productIds)}>{t('selectAll')}</button>
                </div>
                <div className="layout-column-33 fl-left">
                    <Pagination
                        pageSize={pageSize}
                        currentPage={page}
                        onPageChange={onPageChange}
                        totalItems={renewalLines}
                    />
                </div>
                <div className="right layout-column-33">
                    <Button
                        text={t('shop.subscriptions.saveAndExit')}
                        theme={ButtonTheme.POSITIVE_INVERT}
                        className="gild"
                        icon="close"
                        size={ButtonSize.MEDIUM}
                        onClick={() =>
                            RoutingUtils.pushUrl(
                                isCollectionNavigationV2Enabled
                                    ? '/collectionmgmt/subscriptions'
                                    : '/assistant/subscriptions'
                            )
                        }
                    />
                </div>
            </div>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
